import React, { useState } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import Button from '../../components/ui/Button';

/**
 * Contact API test page for troubleshooting 
 */
const ApiTestPage: React.FC = () => {
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const testContactApi = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const testData = {
        name: 'API Test User',
        email: 'test@example.com',
        company: 'Test Company',
        phone: '555-123-4567',
        service: 'ai-testing',
        inquiryType: 'test',
        message: 'This is a test message from the API test page.'
      };
      
      console.log('Testing contact API...');
      
      // 1. First test with a simple OPTIONS request
      const optionsResponse = await fetch('/api/contact', {
        method: 'OPTIONS',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      console.log('OPTIONS response:', {
        status: optionsResponse.status,
        statusText: optionsResponse.statusText,
        headers: Object.fromEntries([...optionsResponse.headers.entries()]),
      });
      
      // 2. Now test with the actual POST request
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testData),
      });
      
      const responseText = await response.text();
      console.log('Response text:', responseText);
      
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        responseData = { text: responseText };
      }
      
      setResult({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries([...response.headers.entries()]),
        data: responseData,
      });
      
      if (!response.ok) {
        setError(`Server responded with status ${response.status}`);
      }
    } catch (err) {
      console.error('API test error:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow py-12 px-4 max-w-4xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg p-8">
          <h1 className="text-3xl font-bold mb-6 text-center">API Test Page</h1>
          <p className="mb-8 text-neutral-600 text-center">
            This page is for testing and troubleshooting the contact form API.
          </p>
          
          <div className="flex justify-center mb-8">
            <Button
              variant="primary"
              size="lg"
              onClick={testContactApi}
              disabled={loading}
            >
              {loading ? 'Testing API...' : 'Test Contact API'}
            </Button>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
              <p className="text-red-700">{error}</p>
            </div>
          )}
          
          {result && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">API Response</h3>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-neutral-100 rounded-lg">
                  <span className="font-medium">Status:</span> {result.status}
                </div>
                <div className="p-3 bg-neutral-100 rounded-lg">
                  <span className="font-medium">Status Text:</span> {result.statusText}
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="font-medium mb-2">Headers:</h4>
                <pre className="bg-neutral-100 p-3 rounded-lg overflow-x-auto text-sm">
                  {JSON.stringify(result.headers, null, 2)}
                </pre>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">Response Data:</h4>
                <pre className="bg-neutral-100 p-3 rounded-lg overflow-x-auto text-sm">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            </div>
          )}
          
          <div className="text-center text-sm text-neutral-500 mt-8">
            This page is for development and testing purposes only.
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ApiTestPage;